import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'motion/react'
import { useAutoAnimate } from '@formkit/auto-animate/react'
import { getProjects, createProject, updateProjectStatus, Project, ProjectStatus, ManualOverrideType } from '../api/client'
import { useNotify } from '../components/notice/useNotify'
import Spinner from '../components/Spinner'
import ProjectForm from '../components/ProjectForm'

const ProjectsPage: React.FC = () => {
  const notify = useNotify()
  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState<ProjectStatus | null>(ProjectStatus.ACTIVE)
  const [showNewProjectForm, setShowNewProjectForm] = useState(false)
  const [gridParent] = useAutoAnimate<HTMLDivElement>()
  const [creating, setCreating] = useState(false)
  const [newProject, setNewProject] = useState({
    title: '',
    description: '',
    emoji: '',
    config: {}
  })
  
  const navigate = useNavigate()

  // Load projects on component mount and when status filter changes
  useEffect(() => {
    fetchProjects()
  }, [statusFilter])

  const fetchProjects = async () => {
    setLoading(true)
    try {
      const fetchedProjects = await getProjects(statusFilter || undefined)
      setProjects(fetchedProjects)
    } catch (err) {
      notify.error('Failed to load projects')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const handleCreateProject = async (e: React.FormEvent) => {
    e.preventDefault()
    if (creating) return
    
    setCreating(true)
    try {
      // Create a properly formatted project object for the API
      const projectToCreate = {
        ...newProject,
        // Remove empty emoji
        emoji: newProject.emoji ? newProject.emoji : undefined
      }
      
      const createdProject = await createProject(projectToCreate)
      setProjects(prev => [createdProject, ...prev])
      setShowNewProjectForm(false)
      setNewProject({
        title: '',
        description: '',
        emoji: '',
        config: {}
      })
    } catch (err) {
      notify.error('Failed to create project: ' + (err.response?.data?.detail || err.message || 'Unknown error'))
      console.error(err)
    } finally {
      setCreating(false)
    }
  }

  const handleStatusChange = async (projectId: number, newStatus: ProjectStatus) => {
    try {
      await updateProjectStatus(projectId, newStatus)
      // Refetch projects to reflect changes
      fetchProjects()
    } catch (err) {
      notify.error('Failed to update project status')
      console.error(err)
    }
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleDateString()
  }

  return (
    <div className="flex flex-col min-h-screen bg-blush-light">
      
      <div className="container mx-auto px-4 pb-8 sm:pt-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <h1 className="text-3xl font-bold text-gray-800">Projects</h1>
          
          <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setStatusFilter(ProjectStatus.ACTIVE)}
                className={`px-3 py-2 rounded-md text-sm ${
                  statusFilter === ProjectStatus.ACTIVE
                    ? 'bg-action text-plum'
                    : 'bg-gray-200 text-gray-700'
                }`}
              >
                Active
              </button>
              <button
                onClick={() => setStatusFilter(ProjectStatus.ARCHIVED)}
                className={`px-3 py-2 rounded-md text-sm ${
                  statusFilter === ProjectStatus.ARCHIVED
                    ? 'bg-action text-plum'
                    : 'bg-gray-200 text-gray-700'
                }`}
              >
                Archived
              </button>
              <button
                onClick={() => setStatusFilter(null)}
                className={`px-3 py-2 rounded-md text-sm ${
                  statusFilter === null
                    ? 'bg-action text-plum'
                    : 'bg-gray-200 text-gray-700'
                }`}
              >
                All
              </button>
            </div>
            
            <button
              onClick={() => setShowNewProjectForm(!showNewProjectForm)}
              className="bg-action text-plum px-4 py-2 rounded-md hover:bg-action-dark self-start"
            >
              {showNewProjectForm ? 'Cancel' : 'Add Project'}
            </button>
          </div>
        </div>
        
        {/* New project form */}
        {showNewProjectForm && (
          <div className="bg-white p-6 rounded-lg shadow-md mb-8">
            <h2 className="text-xl font-semibold mb-4">Create New Project</h2>
            <ProjectForm
              project={newProject}
              setProject={setNewProject}
              onSubmit={handleCreateProject}
              submitButtonText="Create Project"
              isSubmitting={creating}
            />
          </div>
        )}
        
        {/* Projects list */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Spinner />
          </div>
        ) : projects.length === 0 ? (
          <div className="bg-white p-8 rounded-lg shadow-md text-center">
            <h3 className="text-xl text-gray-700 mb-2">No projects found</h3>
            <p className="text-gray-500">
              {statusFilter ? `You don't have any ${statusFilter} projects.` : "You don't have any projects yet."}
            </p>
            {!showNewProjectForm && (
              <button
                onClick={() => setShowNewProjectForm(true)}
                className="mt-4 bg-action text-plum px-4 py-2 rounded-md hover:bg-action-dark"
              >
                Create your first project
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" ref={gridParent}>
            {projects.map((project) => (
              <motion.div
                key={project.id}
                className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => navigate(`/projects/${project.id}`)}
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              >
                <div className="flex justify-between items-start mb-3">
                  <h3 className="text-xl font-semibold text-gray-800">
                    {project.emoji && <span className="mr-2">{project.emoji}</span>}
                    {project.title}
                  </h3>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    project.status === ProjectStatus.DRAFT
                      ? 'bg-yellow-100 text-yellow-800'
                      : project.status === ProjectStatus.ACTIVE
                      ? 'bg-green-100 text-green-800'
                      : project.status === ProjectStatus.COMPLETED
                      ? 'bg-amber-100 text-amber-900'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {project.status}
                  </span>
                </div>
                
                {project.description && (
                  <p className="text-gray-600 mb-4 line-clamp-3">{project.description}</p>
                )}
                
                {/* Project Categories */}
                {project.categories && project.categories.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {project.categories.filter(c => c.manual_override !== ManualOverrideType.DELETE).map((category) => (
                      <span
                        key={category.id}
                        className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-900"
                        title={category.description || category.name}
                      >
                        {category.icon && <span className="mr-1 text-xs">{category.icon}</span>}
                        {category.name}
                      </span>
                    ))}
                  </div>
                )}
                
                <div className="flex flex-col space-y-2">
                  {project.start_date && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Start Date:</span>
                      <span className="font-medium text-gray-700">{formatDate(project.start_date)}</span>
                    </div>
                  )}
                  
                  {project.end_date && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">End Date:</span>
                      <span className="font-medium text-gray-700">{formatDate(project.end_date)}</span>
                    </div>
                  )}
                </div>
                
                <div className="mt-6 flex justify-end space-x-2" onClick={(e) => e.stopPropagation()}>
                  {project.status === ProjectStatus.ACTIVE && (
                    <button
                      onClick={() => handleStatusChange(project.id, ProjectStatus.ARCHIVED)}
                      className="px-3 py-1 bg-secondary text-gray-700 rounded hover:bg-secondary-dark"
                    >
                      Archive
                    </button>
                  )}

                  {project.status === ProjectStatus.ARCHIVED && (
                    <button
                      onClick={() => handleStatusChange(project.id, ProjectStatus.ACTIVE)}
                      className="px-3 py-1 bg-amber-100 text-amber-900 rounded hover:bg-amber-200"
                    >
                      Unarchive
                    </button>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default ProjectsPage